import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { GradientSpinner } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';
import { saveTwitterAuth } from '@/core/services/twitter';

const TWITTER_RETURN_URL_KEY = 'twitter_return_url';
const TWITTER_TOKEN_SECRET_KEY = 'twitter_oauth_token_secret';

/**
 * X（Twitter）OAuth コールバックページ
 *
 * X からリダイレクトで戻ってきたときに認証トークンを処理し、
 * 元のページにリダイレクトする。
 */
export function TwitterCallbackPage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const oauthToken = searchParams.get('oauth_token');
    const oauthVerifier = searchParams.get('oauth_verifier');

    // 認証拒否された場合
    if (!oauthVerifier && oauthToken) {
      setError('X連携がキャンセルされました');
      return;
    }

    if (oauthToken && oauthVerifier && !processing && user) {
      setProcessing(true);

      const oauthTokenSecret = sessionStorage.getItem(TWITTER_TOKEN_SECRET_KEY);
      if (!oauthTokenSecret) {
        setError('認証情報が見つかりません。もう一度お試しください。');
        return;
      }

      saveTwitterAuth({
        oauthToken,
        oauthVerifier,
        oauthTokenSecret,
      })
        .then(() => {
          sessionStorage.removeItem(TWITTER_TOKEN_SECRET_KEY);
          setSuccess(true);
        })
        .catch((err) => {
          console.error('Twitter auth error:', err);
          setError('X連携に失敗しました');
        });
    }
  }, [searchParams, processing, user]);

  // ログインしていない
  if (!authLoading && !user) {
    return <Navigate to="/" replace />;
  }

  // 成功 → 元のページへリダイレクト
  if (success) {
    const savedReturnUrl = sessionStorage.getItem(TWITTER_RETURN_URL_KEY);
    sessionStorage.removeItem(TWITTER_RETURN_URL_KEY);
    const redirectTo = savedReturnUrl ? `${savedReturnUrl}?twitter=success` : '/';
    return <Navigate to={redirectTo} replace />;
  }

  // エラー時
  if (error) {
    const savedReturnUrl = sessionStorage.getItem(TWITTER_RETURN_URL_KEY);
    sessionStorage.removeItem(TWITTER_RETURN_URL_KEY);
    const redirectTo = savedReturnUrl || '/';
    return <Navigate to={`${redirectTo}?twitter_error=${encodeURIComponent(error)}`} replace />;
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <GradientSpinner />
        <Typography color="text.secondary">X連携処理中...</Typography>
      </Box>
    </Container>
  );
}
